<template>
  <wo-region>
    <wo-layout>
      <wo-grid>
        <wo-column width-lg="8">
          <span>{{ currentUser.company.name }}</span>
          <wo-title tag-name="h1" tag-style="h1" mod-block>Performance</wo-title>
        </wo-column>
        <wo-column v-if="performanceEnabled" width-lg="4" v-wo-v-align:center.md v-wo-align:right.lg>
          <po-toggle-button v-model="performanceType" :options="performanceTypes" />
          <wo-spacer mod-xl />
        </wo-column>
      </wo-grid>
      <wo-grid v-if="performanceEnabled">
        <wo-column v-for="(type, index) in valueTypes" :key="type.id" width="4" width-sm="3" width-lg="2">
          <wo-description-data>
            <wo-description-data-label>
              <span v-t="type.labelKey" /><wo-icon v-if="index < 2" icon="ring" :mod-primary="index === 0" :mod-secondary="index === 1"></wo-icon>
              <span v-if="index === 2"> - - -</span>
            </wo-description-data-label>
            <wo-description-data-value>
              <wo-title tag-name="span" tag-style="h4">{{ type.displayValue }} </wo-title>
            </wo-description-data-value>
          </wo-description-data>
        </wo-column>
      </wo-grid>
      <wo-alert v-else-if="performance"><span v-t="'report.performance.noData'" /></wo-alert>

    </wo-layout>
    <po-loading v-if="!performance" mod-layover />

    <wo-chart v-if="performanceEnabled" :options="chartOptions" />

    <wo-layout v-if="categories">
      <wo-spacer mod-xl />
      <wo-card :title="categoryTitle">
        <po-table>
          <po-table-header slot="header" :columns="categoryColumns" />
          <template slot="body">
            <tr v-for="item in categories" :key="item.name">
              <td>{{ item.name }}</td>
              <td v-wo-align:right>{{ format(item.actual) }}</td>
              <td v-if="forecast && !forecast.virtual" v-wo-align:right>{{ format(item.toDate.forecast) }}</td>
              <td v-if="budget" v-wo-align:right>{{ format(item.toDate.budget) }}</td>
              <td v-if="budget" v-wo-align:right>{{ format(item.toDate.delta) }}</td>
              <td v-if="budget" v-wo-align:right v-wo-mark-table-cell>{{ item.toDate.deltaPercentage | percentage(true) }}</td>
            </tr>
          </template>
        </po-table>
      </wo-card>
    </wo-layout>

  </wo-region>
</template>

<script>

import _ from 'lodash'
import moment from 'moment'
import numeral from 'numeral'
import { mapState } from 'vuex'

import { Enums } from '@/model/util'

import { PerformanceValueType } from '@/model/report'
import { companyPerformanceRepository } from '@/repository/report'

const performanceTypes = [
  Enums.value('volume', 'report.performance.volume', { format: v => `${v} L` }),
  Enums.value('turnover', 'report.performance.turnover', { format: v => `€ ${v}` })
]

const allValueTypes = [PerformanceValueType.values.ACTUAL, PerformanceValueType.values.FORECAST, PerformanceValueType.values.BUDGET]

export default {
  props: {
    type: String
  },
  data () {
    return {
      performanceType: performanceTypes[0],
      year: moment().year(),
      actualMonths: moment().month() + 1
    }
  },
  computed: mapState({
    currentUser: state => state.identity.currentUser,
    performance: state => state.report.companyPerformanceList,
    performanceTypes: () => performanceTypes,
    categories () {
      const orders = _.map(this.valueTypes, v => v.id)
      return this.performanceEnabled && _.orderBy(this.performance.items, orders, _.times(orders.length, o => 'desc'))
    },
    categoryTitle () {
      return this.$t('report.performance.categoryTitle', { type: this.$t(this.performanceType.labelKey) })
    },
    categoryColumns () {
      const colums = [{ labelKey: 'report.performance.category' }]
      _.each(_.filter(this.valueTypes, t => !t.virtual), t => colums.push({ ...t, align: 'right' }))

      if (this.budget) {
        colums.push({ labelKey: 'report.performance.delta', align: 'right' })
        colums.push({ name: '%', align: 'right', mark: true })
      }
      return colums
    },
    valueTypes () {
      return _.filter([this.actual, this.forecast, this.budget])
    },
    actual () {
      return this.createPerformanceData(PerformanceValueType.values.ACTUAL, true)
    },
    forecast () {
      return this.createPerformanceData(PerformanceValueType.values.FORECAST, false, PerformanceValueType.values.BUDGET)
    },
    budget () {
      return this.createPerformanceData(PerformanceValueType.values.BUDGET)
    },
    performanceEnabled () {
      return this.currentUser.company && this.currentUser.company.account_number && this.performance && !_.isEmpty(this.performance.items)
    },
    monthlyTotal () {
      const total = _.times(12, i => ({ value: this.createPerformanceHolder() }))
      _.each(this.performance.items, item => {
        _.each(item.month_performances, p => {
          _.each(allValueTypes, t => {
            if (p[t.id]) {
              total[p.month - 1].value[t.id] += Math.ceil(p[t.id])
            }
          })
        })
      })
      return total
    },
    chartOptions () {
      return this.createChartOptions()
    },
    filter () {
      return {
        type: this.performanceType.id,
        year: this.year
      }
    }
  }),
  created () {
    this.loadPerformance()
  },
  watch: {
    filter () {
      this.loadPerformance()
    }
  },
  methods: {
    loadPerformance () {
      this.$store.dispatch(companyPerformanceRepository.findAll, { companyId: this.currentUser.company.id, filter: this.filter })
    },
    createPerformanceData (type, includeZero, fallBackType) {
      let value = this.performance && _.sumBy(this.performance.items, `toDate.${type.id}`)
      let virtual = false
      if (!value && this.performance && fallBackType) {
        value = _.sumBy(this.performance.items, `toDate.${fallBackType.id}`)
        virtual = true
      }
      return value || includeZero ? { ...type, value, displayValue: this.format(value), virtual } : undefined
    },
    cumulate (values) {
      let sum = 0
      return _.map(values, v => {
        sum += v
        return sum
      })
    },
    createPerformanceHolder () {
      const result = {}
      _.each(allValueTypes, t => {
        result[t.id] = 0
      })
      return result
    },
    format (value) {
      const formatted = numeral(value).format('0,000')
      return this.performanceType.format(formatted)
    },
    getSerieData () {
      let base = this.cumulate(_.map(this.monthlyTotal, t => t.value[PerformanceValueType.values.ACTUAL.id]))
      if (this.valueTypes.length > 1) {
        const property = this.forecast && !this.forecast.virtual ? PerformanceValueType.values.FORECAST.id : PerformanceValueType.values.BUDGET.id
        const future = this.cumulate(_.map(this.monthlyTotal, t => t.value[property]))
        base = base.slice(0, this.actualMonths).concat(future.slice(this.actualMonths))
      }

      return _.map(base, (v, i) => {
        return {
          y: v,
          displayValue: this.format(v),
          color: i >= this.actualMonths ? 'highcharts-color-1' : 'highcharts-color-0'
        }
      })
    },
    createChartOptions () {
      const actualMonths = this.actualMonths
      const secondaryDimension = this.valueTypes.length > 1 ? this.$t(this.valueTypes[1].labelKey) : undefined

      return {
        chart: {
          type: 'areaspline',
          spacingTop: 50,
          spacingBottom: 50,
          spacingLeft: 0,
          spacingRight: -10,
          height: '525px'
        },
        legend: {
          enabled: false
        },
        plotOptions: {
          series: {
            pointPlacement: 'on',
            marker: {
              enabled: false
            },
            zoneAxis: 'x',
            zones: [{
              value: this.actualMonths - 1,
              className: 'highcharts-zone-0', // for markers
              colorIndex: 0 // for general styling (area),
            }, {
              className: 'highcharts-zone-1',
              colorIndex: 1
            }]
          }
        },
        series: [
          {
            name: 'Performance',
            data: this.getSerieData()
          }
        ],
        tooltip: {
          headerFormat: '',
          pointFormatter () {
            let suffix = ''
            if (this.x >= actualMonths && secondaryDimension) {
              suffix = `<br /> <small>${secondaryDimension}</small>`
            }
            return `<div class="highcharts-tooltip-content ${this.color}"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21 21"class="highcharts-tooltip-icon"><path fill-rule="nonzero" d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15zm0-3a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9z"/></svg> <span class="highcharts-tooltip-text"> ${this.displayValue}${suffix}</span></div>`
          },
          shared: false,
          useHTML: true,
          valueSuffix: ' L'
        },
        xAxis: {
          categories: _.times(12, index => moment().month(index).format('MMMM \'YY')),
          crosshair: {
            width: 1000
          },
          showFirstLabel: false,
          showLastLabel: false,
          plotLines: [{
            value: this.actualMonths - 1,
            label: {
              align: 'center',
              verticalAlign: 'bottom',
              rotation: 0,
              text: 'Today',
              y: 45
            }
          }]
        },
        yAxis: {
          labels: {
            x: 75,
            y: 25
          },
          showFirstLabel: false,
          showLastLabel: false,
          title: {
            text: ''
          },
          plotLines: this.budget && [{
            value: this.budget.value,
            className: 'highcharts-plot-line-dashed'
          }]
        }
      }
    }
  }
}
</script>`
